import type { ArticleData } from '@/mocks/articleData';

interface SeoSchemaProps {
  type: 'home' | 'article';
  article?: ArticleData;
}

const siteOrigin = 'https://vigorbuyspread.rest';
const siteName = 'Vigorbuy Spreadsheet';
const siteDescription = 'Discover the latest Vigorbuy Spreadsheet resources, product guides, QC tips, category lists and smart shopping strategies. Learn how to use Vigorbuy Spreadsheet safely and efficiently.';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg';

function buildHomeSchemas() {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": siteOrigin,
    "description": siteDescription,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteOrigin}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteName,
    "url": siteOrigin,
    "logo": logoUrl,
    "description": siteDescription,
    "sameAs": [
      "https://kaspreadsheet.com"
    ]
  };

  return [websiteSchema, organizationSchema];
}

function buildArticleSchemas(article: ArticleData) {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteOrigin },
      { "@type": "ListItem", "position": 2, "name": article.title, "item": `${siteOrigin}/article/${article.slug}` }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "image": article.imageUrl,
    "author": { "@type": "Organization", "name": siteName },
    "publisher": { "@type": "Organization", "name": siteName, "logo": { "@type": "ImageObject", "url": logoUrl } },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteOrigin}/article/${article.slug}` }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return [breadcrumbSchema, articleSchema, faqSchema];
}

export default function SeoSchema({ type, article }: SeoSchemaProps) {
  const schemas = type === 'home'
    ? buildHomeSchemas()
    : (article ? buildArticleSchemas(article) : []);

  return (
    <>
      {schemas.map((schema, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}